import React, { useContext, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/react';
import { saveAs } from 'file-saver';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  List,
  ListItem,
  Paper,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import Grid from '@mui/material/Grid';
import DialogTitle from '@mui/material/DialogTitle';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import TabList from '@components/common/TabList';
import { useConfirm } from '@components/common/hooks/useConfirm';
import useEventDetailsQuery from '@components/common/hooks/api/useEventDetailsQuery';
import EventParticipantsTable from '@components/ContentComponents/Events/EventParticipantsTable';
import EventDemographics from '@components/ContentComponents/Events/Demographics/EventDemographics';
import { getSessionName } from '@components/Pages/Events/Sessions/sessionFunctions';
import { API_URLS, axiosRequest } from '@lib/axios';
import { NILL, programAreas } from '@lib/constants';
import { PageContext } from '@lib/contexts/pageContext';
import UserContext from '@lib/contexts/userContext';
import moment from '@lib/moment';
import { AbilityContext, Subjects } from '@lib/permissions';
import { formatDateTime, getAnswerLabels, scrollToAlert } from '@lib/utils';
import { getDemographicsSummary, hasCompleteDemographics, shouldTrackParticipants } from '../eventFunctions';
import EventSessionsTable from './EventSessionsTable';
import CompleteEventModal from '@components/ContentComponents/Events/CompleteEvent/CompleteEventModal';
import useFormModal from '@components/common/hooks/useFormModal/useFormModal';
import EventCancelModal from '@components/Pages/Events/EventDetails/EventCancelModal';
import useSessionParticipantsQuery from '@components/common/hooks/api/useSessionParticipantsQuery';

const EventDetails = ({ setTitle }) => {
  const queryClient = useQueryClient();

  const [canceling, setCanceling] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [currentTab, setCurrentTab] = useState('sessions');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');

  const {
    appendErrorMessages,
    setPageMessage,
    setPageErrorMessage,
    setBreadcrumbValue,
  } = useContext(PageContext);
  const { userRecord, authenticating } = useContext(UserContext);

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const { eventID } = useParams();

  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);
  const confirm = useConfirm();

  const formModal = useFormModal();

  const { data: event, isLoading, isError, error, refetch } = useEventDetailsQuery(eventID);
  const { data: participants } = useSessionParticipantsQuery(eventID);

  useEffect(() => {
    let active = true;
    if (isError && active) {
      console.log('[EventDetails] error fetching event:', error);
      setTitle('Error Accessing Page');
    }
    // The active var helps prevent errors due to state changes on unmounted components (when we redirect)
    return () => {
      active = false;
    };
  }, [error, isError, setTitle]);

  useEffect(() => {
    let active = true;
    if (event && active) {
      setBreadcrumbValue(':event', event);
    }
    if (event) {
      event.sessions?.forEach((session) => {
        session.event = event;
        session.name = getSessionName({
          session,
          dateFallback: false,
          useNill: true,
        });
      });
    }

    // The active var helps prevent errors due to state changes on unmounted components (when we redirect)
    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, [event]);

  /**
   * Mutation for canceling an event and all associated sessions
   */
  const cancelMutation = useMutation({
    mutationFn: async (reason) => axiosRequest({
      method: 'PUT',
      url: `/events/${eventID}/cancel`,
      data: { reason }
    }),
    onSuccess: ({ message, redirect }) => {
      setPageErrorMessage('');
      if (redirect === 'details') {
        refetch().then(() => setPageMessage(message));
      } else {
        navigate('/events', { state: { message } });
      }
    },
    onError: (err) => {
      setPageErrorMessage('An error occurred while deleting this event. Please contact <support email address> for assistance.', err);
      console.log('[EventDetails][cancel] error:', err);
      scrollToAlert();
      // Refetch data, in case some sessions were canceled before the error occurred.
      refetch().then();
    },
    onSettled: () => {
      setCanceling(false);
    },
  });

  /**
   * Mutation for updating events. Used by the status change buttons.
   */
  const updateEventMutation = useMutation({
    mutationFn: async (data) => axiosRequest({
      method: 'PUT',
      url: `/events/${eventID}`,
      data,
    }),
    onSuccess: async () => {
      setPageErrorMessage('');
      refetch().then();
      await queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (err) => {
      console.log('[EventDetails][updateEvent] error:', err);
    },
    onSettled: () => {
      setUpdating(false);
    },
  });

  const handleCancel = async () => {
    if (ability.cannot('cancel', subject(Subjects.EVENT, event))) {
      setPageErrorMessage('You do not have permission to cancel this event.');
      return false;
    }

    const hasSessionAttendees = (sessionID) => participants?.some((p) => {
      const attendanceRecord = p.participantAttendance.find(attendance => attendance.session_id === sessionID);
      return attendanceRecord?.status === 'PRESENT';
    });

    const hasAttendees = event.sessions.some((s) => hasSessionAttendees(s.id));

    const statuses = {
      COMPLETED: event.sessions.filter(s => s.status === 'COMPLETED').length,
      INCOMPLETE: event.sessions.filter(s => s.status === 'INCOMPLETE').length,
      CANCELED: event.sessions.filter(s => s.status === 'CANCELED').length,
      total: event.sessions.length,
    };

    let modalProps = null;
    let cancelReason = false;

    /** POPUP 2 - Can't Cancel, Cancel Incomplete Sesssions **/
    if ((statuses.COMPLETED || statuses.CANCELED) && !statuses.INCOMPLETE) {
      confirm({
        title: 'Event cannot be canceled',
        content: (
          <Box sx={{ width: '100%' }} key="content">
            This event cannot be canceled because all sessions for the event are Completed or already Canceled.
          </Box>
        ),
        confirmationText: 'Close',
        hideCancelButton: true,
      });
      return;
    /** POPUP 1 **/
    } else if (statuses.total === 0 || (statuses.INCOMPLETE && !statuses.COMPLETED)) {
      modalProps = {
        title: 'Why is this event canceled?',
        Form: EventCancelModal,
        FormProps: {
          event,
          statuses,
          bottomAlert: "Canceling this event will also cancel all of the event's incomplete sessions.",
        },
        confirmationText: 'Cancel Event',
        cancellationText: 'Keep Event',
      };

      if (hasAttendees) {
        modalProps.FormProps.bottomAlert += ' Session attendance will be reset to 0 for all canceled sessions.';
      }
    /** POPUP 3 **/
    } else if (statuses.INCOMPLETE && statuses.COMPLETED) {
      modalProps = {
        title: 'Event cannot be canceled',
        Form: EventCancelModal,
        FormProps: {
          event,
          statuses,
          topAlert: 'This event cannot be canceled because some of its sessions have been completed. To cancel all incomplete sessions, choose a reason below, then select "Cancel All Incomplete Sessions".',
        },
        confirmationText: 'Cancel All Incomplete Sessions',
        cancellationText: 'Keep Event',
      };

      if (hasAttendees) {
        // modified text
        modalProps.FormProps.topAlert += ' Session attendance will be reset to 0 for all canceled sessions.';
      }
    }

    if (modalProps) {
      cancelReason = await formModal(modalProps).then((data) => data).catch(() => false);
      if (cancelReason) {
        setCanceling(true);
        let reason = cancelReason.cancel_reason;
        reason = reason === 'Other' ? `Other: ${cancelReason.other_text}` : reason;
        cancelMutation.mutate(reason);
      }
    }
  };

  /**
   * Handler for completing or canceling an event
   */
  const handleComplete = async () => {

    if (ability.cannot('edit', subject(Subjects.EVENT, event))) {
      setDialogTitle('This Event Cannot Be Marked Completed');
      setDialogMessage('You do not have permission to edit this event.');
      setDialogOpen(true);
      return false;
    }

    // Events can't be completed if there are no sessions
    if (!event.sessions?.length) {
      setDialogTitle('This Event Cannot Be Marked Completed');
      setDialogMessage('This event cannot be marked completed because there are no sessions. ' +
        'Please add sessions or delete this event if it is no longer needed.');
      setDialogOpen(true);
      return;
    }

    let newStatus = null;
    let comments = null;
    if (event.sessions?.length && event.sessions?.every(session => session.status === 'CANCELED')) {
      // If all sessions are canceled, show the "Cancel event" dialog.

      newStatus = await confirm({
        title: 'This Event Cannot Be Marked Completed',
        content: <Typography variant="body1">
          This event cannot be marked completed because all sessions are canceled. Please select
          &quot;Cancel Event&quot; if this event has been canceled.
        </Typography>,
        confirmationText: 'Cancel Event',
        cancellationText: 'Close',
      })
        .then(() => 'CANCELED')
        .catch(() => null);
      if (!newStatus) {
        return;
      }
    } else {

      // All sessions must be completed or canceled.
      if (event.sessions?.find(session => session.status !== 'COMPLETED' && session.status !== 'CANCELED')) {
        setDialogTitle('This Event Cannot Be Marked Completed');
        setDialogMessage('This event cannot be marked completed because there are one or more incomplete sessions. ' +
          'To complete an event, please make sure all sessions are marked completed or canceled.');
        setDialogOpen(true);
        return;
      }

      // Demographic counts must be recorded for all Class events.
      if (event.event_type === 'CLASS') {
        let content = null;
        if (!hasCompleteDemographics(event)) {
          content = <>
            <Typography variant="body1">
              This event cannot be marked completed because the demographic counts have not been entered.
              Click &quot;Edit Demographics&quot; to enter demographics for this event.
            </Typography>
          </>;
        }
        const demoSummary = getDemographicsSummary(event);
        if (!demoSummary.valid) {
          content = <>
            <Typography variant="body1">
              The demographics for this event require your attention. Click &quot;Edit Demographics&quot; to verify the
              demographic data received.
            </Typography>
          </>;
        }

        if (content) {
          await confirm({
            title: 'This Event Cannot Be Marked Completed',
            content,
            confirmationText: 'Edit Demographics',
            cancellationText: 'Close',
          })
            .then(() => navigate(`/events/${eventID}/complete`))
            .catch(() => false);
          return;
        }
      }

      if (newStatus !== 'CANCELED') {
        comments = await formModal({
          title: 'Mark Event Complete',
          Form: CompleteEventModal,
          FormProps: { event },
          confirmationText: 'Yes, Complete Event',
          cancellationText: 'No',
        })
          .then(({ comments }) => comments)
          .catch(() => false);

        if (comments === false) {
          return;
        }
        newStatus = 'COMPLETED';
      }
    }

    const input = {
      status: newStatus,
    };
    if (newStatus === 'COMPLETED') {
      input.completed_by = userRecord.email;
      input.completedAt = moment().toISOString(true);
      input.comments = comments ?? '';
    }
    if (newStatus === 'CANCELED') {
      input.canceled_by = userRecord.email;
      input.canceledAt = moment().toISOString(true);
    }

    setUpdating(true);
    setPageMessage('');
    try {
      await updateEventMutation.mutateAsync(input);
      if (newStatus === 'CANCELED') {
        setPageMessage('Event Canceled.');
      }
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      await refetch();
    } catch (error) {
      setPageErrorMessage('Error changing event status.', error);
      appendErrorMessages(error.errors);
    }
    setUpdating(false);
  };

  /**
   * Handler for undoing the Completed or Canceled status and changing status back to Incomplete
   */
  const handleSetIncomplete = async () => {
    const options = event.status === 'COMPLETED' ?
      {
        title: 'Undo Completed Status',
        content: (
          <Box sx={{ width: '100%' }} key="content">
            <p>When you undo the completed status for this event, it will change from Completed to Incomplete and staff
              will be able to resume editing.</p>
            <p>Are you sure you want to undo the completed status for this event?</p>
          </Box>
        ),
        confirmationText: 'Yes, Undo Completed Status',
        cancellationText: 'No',
      } : {
        title: 'Undo Canceled Status',
        content: (
          <Box sx={{ width: '100%' }} key="content">
            <p>When you undo the canceled status for this event, it will change from Canceled to Incomplete and staff
              will be able to resume editing.</p>
            <p>The canceled status for sessions will not be affected - if you wish to undo cancelation for a session, you must do so per session.</p>
            <p>Are you sure you want to undo the canceled status for this event?</p>
          </Box>
        ),
        confirmationText: 'Yes, Undo Canceled Status',
        cancellationText: 'No',
      };

    const shouldChangeStatus = await confirm(options)
      .then(() => true)
      .catch(() => false);

    if (shouldChangeStatus) {
      const input = {
        status: 'INCOMPLETE',
        completed_by: null,
        completedAt: null,
        canceled_by: null,
        canceledAt: null,
      };
      try {
        await updateEventMutation.mutateAsync(input);
        setPageMessage('Event status restored to INCOMPLETE.');
        // If user was on the Demographics tab, it will disappear, so switch them to the Sessions tab.
        setCurrentTab('sessions');
      } catch (error) {
        appendErrorMessages('Error changing event status.');
        appendErrorMessages(error.errors.map(e => e.message));
      }
    }
  };

  const handleDownloadClick = async () => {
    // We request the file via the API, in order to set the auth headers. Then we use the
    // `saveAs` utility from file-saver to cause the browser to download the file.
    try {
      const file_response = await axiosRequest({
        method: 'GET',
        url: `${API_URLS.EVENTS}/${event.id}/export-participants`,
        responseType: 'blob'
      });

      const now = moment();
      saveAs(file_response, `ParticipantDemographics_${now.format('YYYYMMDDHHmmss')}.xlsx`);
    } catch (e) {
      console.error('Failed to download file', e);
    }
  }

  const preventAddingSession = () => {
    setDialogTitle('Booth events can only have one session per event');
    setDialogMessage('If you want to schedule another booth, return to the Events page and' +
      ' select "Create Event" then add the session to the new event.');
    setDialogOpen(true);
  };

  // Admins can still edit Completed events. Staff users can't.
  const canEdit = event && ability.can('edit', subject(Subjects.EVENT, event));

  const canAddSession = event && (event.event_type !== 'BOOTH' || !event.sessions.find(s => s.status !== 'CANCELED'));

  const tabs = [
    {
      name: 'sessions',
      label: 'SESSIONS',
    },
  ];
  if (event?.status === 'COMPLETED') {
    tabs.push({
      name: 'demographics',
      label: 'DEMOGRAPHICS',
    });
    if (shouldTrackParticipants(event)) {
      tabs.push({
        name: 'participants',
        label: 'PARTICIPANTS',
      });
    }
  }

  const participantNeeds = getAnswerLabels(event?.event_answers?.['participant_needs'] ?? []);
  const project = getAnswerLabels(event?.event_answers?.['project'] ?? []);

  return (
    <div id="pagewrapper" className={'with-two-sidebar'} style={{ padding: 0 }}>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        disableRestoreFocus
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button id="dialog-close" variant="contained" onClick={handleDialogClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <div id="content-wrapper" className={'event-details-card'}>
        {isLoading && (
          <Grid item xs={12} textAlign="center">
            <CircularProgress />
          </Grid>
        )}
        {!isLoading && event && (
          <>
            {event.status === 'COMPLETED' && (
              <Alert severity="success" sx={{ marginBottom: '10px', position: 'relative' }}>
                This event is completed.
                <br />
                Completed on: {formatDateTime(event.completedAt)} by {event.completed_by_names || 'unknown user'}
                <Button
                  variant="contained"
                  onClick={handleSetIncomplete}
                  disabled={!canEdit}
                  sx={{
                    display: 'inline-block',
                    position: 'absolute',
                    right: '10px',
                    bottom: '10px',
                  }}
                >
                  Undo Completed Status
                </Button>
              </Alert>
            )}

            {event.status === 'CANCELED' && (
              <Alert severity="warning" sx={{ marginBottom: '10px', position: 'relative' }}>
                This event is canceled.
                <br />
                Cancellation Reason: {event.cancel_reason}
                <br />
                Canceled on: {formatDateTime(event.canceledAt)} by {event.canceled_by_names || 'unknown user'}
                {canEdit && (
                  <Button
                    variant="contained"
                    onClick={handleSetIncomplete}
                    disabled={!canEdit}
                    sx={{
                      display: 'inline-block',
                      position: 'absolute',
                      right: '10px',
                      bottom: '10px',
                    }}
                  >
                    Undo Canceled Status
                  </Button>
                )}
              </Alert>
            )}

            <Paper elevation={3} id="event-detail">
              <Box padding={3}>
                <Typography variant="h1" color="ilstorm.main">
                  {event.event_name}
                </Typography>
                {event.is_test && (
                  <Chip label="Test Event" />
                )}
                <List dense={true}>
                  <ListItem disablePadding>
                    Grants &amp; Special Projects: {project[0] || NILL}
                  </ListItem>
                  {event.curriculum && (
                    <ListItem disablePadding>
                      Primary Curriculum: {(event?.curriculum?.custom ? event?.curriculum_name : null) ?? event?.curriculum?.name} ({event?.curriculum?.audience})
                    </ListItem>
                  )}
                  <ListItem disablePadding>
                    Partner Site: {event.event_partner}
                  </ListItem>
                  {participantNeeds.length > 0 && (
                    <ListItem disablePadding>
                      Specific Needs: {participantNeeds.join(', ')}
                    </ListItem>
                  )}
                  <ListItem disablePadding>
                    Created By: {event.created_by_names}
                  </ListItem>
                  <ListItem disablePadding>
                    Last Modified: {`${event.updated_by_names}, ${formatDateTime(event.updatedAt)}`}
                  </ListItem>
                  {event?.status === 'COMPLETED' && (
                    <ListItem disablePadding sx={{ marginTop: '12px' }}>
                      Comments: {event.comments || 'No comments provided.'}
                    </ListItem>
                  )}
                </List>
                <Stack direction="row" justifyContent="space-between" sx={{ marginTop: '20px' }}>
                  <Button
                    id="edit-event-button"
                    variant="contained"
                    startIcon={<EditIcon />}
                    component={RouterLink}
                    to={canEdit ? `/events/edit/${eventID}` : 'javascript:void(0);'}
                    disabled={!canEdit}
                  >
                    Edit Event
                  </Button>
                  <div>
                    {(event.status !== 'CANCELED' && event.status !== 'COMPLETED') && (
                      <Button
                        id="cancel-event-button"
                        variant="contained"
                        color="error"
                        startIcon={<ClearIcon />}
                        disabled={canceling || !canEdit}
                        onClick={handleCancel}
                      >
                        Cancel Event
                      </Button>
                    )}
                    {event.status !== 'COMPLETED' && event.status !== 'CANCELED' && (
                      <Button
                        id="complete-event-button"
                        variant="contained"
                        color="success"
                        startIcon={<CheckIcon />}
                        disabled={updating || !canEdit}
                        onClick={handleComplete}
                        sx={{ marginLeft: '20px' }}
                      >
                        Complete Event
                      </Button>
                    )}

                    {/* Admin-only participant download link */}
                    {ability.can('view_all', Subjects.EVENT) &&
                      shouldTrackParticipants(event) &&
                      event.status === 'COMPLETED' && (
                      <Button
                        id="download-participants-button"
                        variant="contained"
                        color="primary"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadClick}
                      >
                        Download Participant Demographics
                      </Button>
                    )}
                  </div>
                </Stack>
              </Box>
            </Paper>

            <TabList tabs={tabs} currentTab={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} />

            {currentTab === 'sessions' && (
              <Box mt="28px" role={tabs.length > 1 ? 'tabpanel' : ''}>
                {!canEdit && (
                  <Button
                    id="add-session-button"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                    }}
                    disabled={true}
                  >
                    Add Session
                  </Button>
                )}
                {canEdit && canAddSession && (
                  <Button
                    id="add-session-button"
                    variant="contained"
                    startIcon={<AddIcon />}
                    component={RouterLink}
                    to={`/events/${eventID}/sessions/add`}
                  >
                    Add Session
                  </Button>
                )}
                {canEdit && !canAddSession && (
                  <Button
                    id="add-session-button"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={preventAddingSession}
                  >
                    Add Session
                  </Button>
                )}
                <EventSessionsTable event={event} sessions={event.sessions} />
              </Box>
            )}
            {currentTab === 'demographics' && (
              <Box mt="28px" role={tabs.length > 1 ? 'tabpanel' : ''}>
                <EventDemographics event={event} showEdit={false} />
              </Box>
            )}
            {currentTab === 'participants' && (
              <Box mt="28px" role={tabs.length > 1 ? 'tabpanel' : ''}>
                <EventParticipantsTable event={event} sessions={event.sessions} />
              </Box>
            )}
          </>
        )}
        {(!authenticating && !isLoading && !event) && (
          <>
            You do not have access, or the event was not found.
          </>
        )}
      </div>
    </div>
  );
};

export default EventDetails;
